/* ==========================================================================
   custom.js — ShopAvail
   Loaded after Horizon's own scripts. Additive only: never redefines a theme
   component, only fills a gap Horizon leaves open.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Sticky add-to-cart: show it when the buy button is BELOW the fold.

   Horizon's sticky-add-to-cart.js only reveals the bar once the buy buttons
   have been scrolled PAST — its own comment says so:

       // If rect.top >= 0, element is below viewport - don't show sticky bar yet

   On a product page where the gallery pushes the CTA under the fold, that
   means a shopper has to scroll the whole page before any buy button appears.
   This handles exactly the case Horizon refuses: buy buttons entirely below
   the viewport while the shopper has begun scrolling.

   It sets the same `data-stuck` attribute Horizon's #showStickyBar() sets, so
   there is no second visual system, and it stands down as soon as the real
   buy buttons are on screen.
   -------------------------------------------------------------------------- */
(function stickyCtaBelowFold() {
  var SCROLL_TRIGGER = 120; // px scrolled before the bar is allowed to appear

  function init() {
    var bar = document.querySelector('.sticky-add-to-cart__bar');
    var buyButtons = document.querySelector('.buy-buttons-block');
    if (!bar || !buyButtons) return;

    var queued = false;

    function evaluate() {
      queued = false;
      var rect = buyButtons.getBoundingClientRect();
      var viewportH = window.innerHeight || document.documentElement.clientHeight;

      var onScreen = rect.bottom > 0 && rect.top < viewportH;
      var entirelyBelow = rect.top >= viewportH;

      if (onScreen) {
        // Real CTA is visible: the sticky bar is redundant.
        bar.setAttribute('data-stuck', 'false');
      } else if (entirelyBelow && window.scrollY > SCROLL_TRIGGER) {
        // Horizon's own observer declines this case; cover it here.
        bar.setAttribute('data-stuck', 'true');
      }
      // Scrolled past (rect.bottom <= 0) is left to Horizon's observer.
    }

    function onScroll() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(evaluate);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    evaluate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* --------------------------------------------------------------------------
   Quantity tiers as a stacked card list.

   Horizon renders variant options as a row of small pill buttons showing only
   the value name. For a volume ladder that hides the thing that sells it: the
   price and the saving. This upgrades ONLY an option named "Quantity" into a
   vertical stack of selectable cards, each showing the tier name, its price,
   the struck-through reference price and the saving.

   It decorates Horizon's existing radio inputs rather than replacing them, so
   variant selection, availability and the add-to-cart form all keep working
   exactly as the theme intends. Prices come from #qty-tier-data, emitted by
   snippets/quantity-tier-data.liquid.
   -------------------------------------------------------------------------- */
(function quantityTierCards() {
  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // Tier names carry their unit count ("Buy 2", "8 Pods", "3 Strips"),
  // so the per-unit price can be derived without extra data.
  function unitsIn(name) {
    var m = /(\d+)/.exec(name);
    return m ? parseInt(m[1], 10) : 1;
  }

  var observer = null;

  function decorate() {
    var dataEl = document.getElementById('qty-tier-data');
    if (!dataEl) return;

    var tiers;
    try {
      tiers = JSON.parse(dataEl.textContent).tiers || [];
    } catch (e) {
      return;
    }
    if (!tiers.length) return;

    var byName = {};
    tiers.forEach(function (t) { byName[t.name] = t; });

    var groups = document.querySelectorAll('.variant-option');
    var target = null;
    for (var i = 0; i < groups.length; i++) {
      var legend = groups[i].querySelector('legend, label');
      if (legend && /^\s*(quantity|set)\s*$/i.test((legend.textContent || '').trim())) {
        target = groups[i];
        break;
      }
    }
    if (!target) return;

    target.classList.add('qty-tiers');

    var labels = target.querySelectorAll('.variant-option__button-label');
    for (var j = 0; j < labels.length; j++) {
      var label = labels[j];
      if (label.dataset.tierDecorated === 'true') continue;

      var textEl = label.querySelector('.variant-option__button-label__text');
      var name = (textEl ? textEl.textContent : label.textContent).trim();
      var tier = byName[name];
      if (!tier) continue;

      var units = unitsIn(name);
      var hasDeal = tier.compare && tier.compare > tier.price;

      // Build an explicit two-line card. Grid areas alone forced one row per
      // datum, which made the cards unnecessarily tall.
      var check = document.createElement('span');
      check.className = 'qty-tier__check';
      check.setAttribute('aria-hidden', 'true');

      var body = document.createElement('span');
      body.className = 'qty-tier__body';

      var line1 = document.createElement('span');
      line1.className = 'qty-tier__line1';
      if (textEl) line1.appendChild(textEl);
      else line1.textContent = name;

      if (j === 1 && labels.length > 1) {
        var pop = document.createElement('span');
        pop.className = 'qty-tier__badge';
        pop.textContent = 'Most Popular';
        label.appendChild(pop);
        label.classList.add('qty-tier--popular');
      }

      var line2 = document.createElement('span');
      line2.className = 'qty-tier__line2';
      if (hasDeal) {
        var save = document.createElement('span');
        save.className = 'qty-tier__save';
        save.textContent = 'Save ' + money(tier.compare - tier.price);
        line2.appendChild(save);
      }
      if (units > 1) {
        var each = document.createElement('span');
        each.className = 'qty-tier__each';
        each.textContent = money(Math.round(tier.price / units)) + ' each';
        line2.appendChild(each);
      }

      body.appendChild(line1);
      if (line2.childNodes.length) body.appendChild(line2);

      var meta = document.createElement('span');
      meta.className = 'qty-tier__meta';
      var price = document.createElement('span');
      price.className = 'qty-tier__price';
      price.textContent = money(tier.price);
      meta.appendChild(price);
      if (hasDeal) {
        var was = document.createElement('span');
        was.className = 'qty-tier__was';
        was.textContent = money(tier.compare);
        meta.appendChild(was);
      }

      label.appendChild(check);
      label.appendChild(body);
      label.appendChild(meta);
      label.dataset.tierDecorated = 'true';
    }
  }

  /* Horizon re-renders the variant picker through the Section Rendering API
     on every selection, which replaces the labels and drops anything injected
     into them. That is what made the selector flicker and change. Watching the
     picker subtree and re-decorating covers every re-render path, rather than
     guessing at which event the theme fires. The observer is detached while
     decorating so our own DOM writes cannot retrigger it. */
  function run() {
    if (observer) observer.disconnect();
    decorate();
    var picker = document.querySelector('variant-picker, .variant-picker');
    if (picker && observer) observer.observe(picker, { childList: true, subtree: true });
  }

  function init() {
    observer = new MutationObserver(function () {
      window.requestAnimationFrame(run);
    });
    run();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
